import React, { useEffect, useState } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useAuth } from './hooks/useAuth';
import { Transaction, Period, Bill, TransactionType, AccountRequest } from './types';
import { BottomNav } from './components/BottomNav';
import { FloatingActionButton } from './components/FloatingActionButton';
import { TransactionModal } from './components/TransactionModal';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { TransactionsPage } from './pages/TransactionsPage';
import { DebtsPage } from './pages/DebtsPage';
import { SavingsPage } from './pages/SavingsPage';
import { BillsPage } from './pages/BillsPage';
import { AccountPage } from './pages/AccountPage';
import { v4 as uuidv4 } from 'uuid';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
export function App() {
  const auth = useAuth();
  // User-specific data keys
  const transactionsKey = auth.currentUser ? `transactions_${auth.currentUser.username}` : 'transactions';
  const billsKey = auth.currentUser ? `bills_${auth.currentUser.username}` : 'bills';
  const [transactions, setTransactions] = useLocalStorage<Transaction[]>(transactionsKey, []);
  const [bills, setBills] = useLocalStorage<Bill[]>(billsKey, []);
  const [currentView, setCurrentView] = useState<'home' | 'transactions' | 'debts' | 'savings' | 'bills' | 'account'>('home');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [modalInitialType, setModalInitialType] = useState<TransactionType | null>(null);
  const [period, setPeriod] = useState<Period>('month');
  const [date, setDate] = useState(new Date());
  const [transactionFilter, setTransactionFilter] = useState<TransactionType | null>(null);
  const [debtsActiveTab, setDebtsActiveTab] = useState<'borrowed' | 'lent'>('borrowed');
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  // Check for pending requests (admin only)
  useEffect(() => {
    if (auth.currentUser?.role === 'admin') {
      const stored = localStorage.getItem('account_requests');
      if (stored) {
        const requests = JSON.parse(stored) as AccountRequest[];
        const pending = requests.filter(r => r.status === 'pending');
        setPendingRequestsCount(pending.length);
      }
    }
  }, [auth.currentUser, currentView]);
  // Show login page if not authenticated
  if (!auth.isAuthenticated) {
    return <LoginPage onLogin={auth.login} onBiometricLogin={auth.loginWithBiometric} data-id="element-0" />;
  }
  const handleSaveTransaction = (t: Omit<Transaction, 'id'> & {
    id?: string;
  }) => {
    if (t.id) {
      setTransactions(prev => prev.map(tr => tr.id === t.id ? {
        ...t,
        id: t.id!
      } : tr));
    } else {
      setTransactions(prev => [...prev, {
        ...t,
        id: uuidv4()
      }]);
    }
    setEditingTransaction(null);
  };
  const handleDeleteTransaction = (id: string) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
    setEditingTransaction(null);
  };
  const handleEditClick = (t: Transaction) => {
    setEditingTransaction(t);
    setModalInitialType(null);
    setIsModalOpen(true);
  };
  const handleFabClick = () => {
    setEditingTransaction(null);
    if (currentView === 'savings') {
      setModalInitialType('savings');
    } else if (currentView === 'debts') {
      setModalInitialType(debtsActiveTab === 'borrowed' ? 'income' : 'expense');
    } else {
      setModalInitialType(null);
    }
    setIsModalOpen(true);
  };
  const handleAddBill = (bill: Omit<Bill, 'id'>) => {
    setBills(prev => [...prev, {
      ...bill,
      id: uuidv4()
    }]);
  };
  const handleUpdateBill = (bill: Bill) => {
    setBills(prev => prev.map(b => b.id === bill.id ? bill : b));
  };
  const handleToggleBillPaid = (id: string) => {
    setBills(prev => prev.map(b => b.id === id ? {
      ...b,
      isPaid: !b.isPaid,
      lastPaidDate: !b.isPaid ? new Date().toISOString() : b.lastPaidDate
    } : b));
  };
  const handleDeleteBill = (id: string) => {
    setBills(prev => prev.filter(b => b.id !== id));
  };
  const handleViewTransactionsByType = (type: TransactionType) => {
    setTransactionFilter(type);
    setCurrentView('transactions');
  };
  const views: Array<'home' | 'transactions' | 'debts' | 'savings' | 'bills' | 'account'> = ['home', 'transactions', 'debts', 'savings', 'bills', 'account'];
  const currentIndex = views.indexOf(currentView);
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;
    if (info.offset.x > threshold && currentIndex > 0) {
      setCurrentView(views[currentIndex - 1]);
    } else if (info.offset.x < -threshold && currentIndex < views.length - 1) {
      setCurrentView(views[currentIndex + 1]);
    }
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  return <div className="min-h-screen bg-stone-950 text-stone-50 font-sans selection:bg-amber-500/30" data-id="element-1">
      <div className="max-w-md mx-auto min-h-screen bg-stone-950 relative shadow-2xl overflow-hidden" data-id="element-2">
        <header className="px-6 pt-6 pb-2" data-id="element-3">
          <div data-id="element-4">
            <h1 className="text-xl font-bold text-stone-200" data-id="element-5">
              {currentView === 'home' && 'Smart Budget'}
              {currentView === 'transactions' && 'Transactions'}
              {currentView === 'debts' && 'Debts'}
              {currentView === 'savings' && 'Savings'}
              {currentView === 'bills' && 'Bills'}
              {currentView === 'account' && 'Account'}
            </h1>
            <p className="text-xs text-stone-500" data-id="element-6">
              Welcome back, {auth.currentUser?.username}
            </p>
          </div>
        </header>

        <main className="px-4 py-2 overflow-hidden" data-id="element-7">
          <AnimatePresence mode="wait" custom={currentIndex} data-id="element-8">
            <motion.div key={currentView} custom={currentIndex} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragEnd={handleDragEnd} data-id="element-9">
              {currentView === 'home' && <HomePage transactions={transactions} period={period} date={date} onPeriodChange={setPeriod} onDateChange={setDate} onEditTransaction={handleEditClick} onViewAll={() => {
              setTransactionFilter(null);
              setCurrentView('transactions');
            }} onViewByType={handleViewTransactionsByType} data-id="element-10" />}
              {currentView === 'transactions' && <TransactionsPage transactions={transactions} period={period} date={date} onPeriodChange={setPeriod} onDateChange={setDate} onEditTransaction={handleEditClick} typeFilter={transactionFilter} onFilterChange={setTransactionFilter} data-id="element-11" />}
              {currentView === 'debts' && <DebtsPage transactions={transactions} onUpdateTransaction={handleSaveTransaction} onTabChange={setDebtsActiveTab} data-id="element-12" />}
              {currentView === 'savings' && <SavingsPage transactions={transactions} period={period} date={date} onPeriodChange={setPeriod} onDateChange={setDate} onEditTransaction={handleEditClick} data-id="element-13" />}
              {currentView === 'bills' && <BillsPage bills={bills} onAddBill={handleAddBill} onUpdateBill={handleUpdateBill} onTogglePaid={handleToggleBillPaid} onDeleteBill={handleDeleteBill} data-id="element-14" />}
              {currentView === 'account' && <AccountPage currentUser={auth.currentUser!} onLogout={auth.logout} onUpdatePassword={auth.updatePassword} onAddUser={auth.addUser} onDeleteUser={auth.deleteUser} getAllUsers={auth.getAllUsers} transactions={transactions} bills={bills} period={period} date={date} data-id="element-15" />}
            </motion.div>
          </AnimatePresence>
        </main>

        {currentView !== 'account' && <FloatingActionButton onClick={handleFabClick} data-id="element-16" />}
        <BottomNav currentView={currentView} onChange={setCurrentView} pendingRequestsCount={auth.currentUser?.role === 'admin' ? pendingRequestsCount : 0} data-id="element-17" />

        <TransactionModal isOpen={isModalOpen} onClose={() => {
        setIsModalOpen(false);
        setEditingTransaction(null);
        setModalInitialType(null);
      }} onSave={handleSaveTransaction} onDelete={handleDeleteTransaction} initialData={editingTransaction} initialType={modalInitialType} data-id="element-18" />
      </div>
    </div>;
}